import type {
  ReportsSummaryResponse,
  RequestsOverTimeResponse,
  DeliveryBreakdownResponse,
  FormTypeUsageResponse,
  PeakUsageResponse,
  TopAgentsResponse,
  StatusDistributionResponse,
  UserTypeBreakdownResponse,
  LobBreakdownResponse,
  LoginActivityResponse,
  ActivityHistoryResponse,
  ErrorSummaryResponse,
  ErrorsOverTimeResponse,
  DateRange,
} from '../types/reports';

export type { UserTypeBreakdownResponse };

const base = () => process.env.REACT_APP_COIPortal_Url ?? '';

// Detected once at module load — never changes within a session
const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function headers(): HeadersInit {
  return {
    Authorization: `Bearer ${localStorage.getItem('igtoken') ?? ''}`,
    NCToken: localStorage.getItem('NCToken') ?? '',
    Accept: 'application/json',
  };
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${base()}${path}`, { headers: headers(), signal });
  if (!res.ok) throw new Error(`API error ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

function qs(
  dateRange: DateRange,
  dateFrom?: string,
  dateTo?: string,
  extra?: Record<string, string>,
) {
  const params: Record<string, string> = { dateRange, tz: USER_TZ };
  if (dateRange === 'custom' && dateFrom) params.dateFrom = dateFrom;
  if (dateRange === 'custom' && dateTo)   params.dateTo   = dateTo;
  if (extra) Object.assign(params, extra);
  return `?${new URLSearchParams(params).toString()}`;
}

export const reportsApi = {
  getSummary: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<ReportsSummaryResponse>(
      `/api/IGIGCOI/reports/summary${qs(dateRange, dateFrom, dateTo)}`, signal),

  getRequestsOverTime: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<RequestsOverTimeResponse>(
      `/api/IGIGCOI/reports/requests-over-time${qs(dateRange, dateFrom, dateTo)}`, signal),

  getDeliveryBreakdown: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<DeliveryBreakdownResponse>(
      `/api/IGIGCOI/reports/delivery-breakdown${qs(dateRange, dateFrom, dateTo)}`, signal),

  getFormTypeUsage: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<FormTypeUsageResponse>(
      `/api/IGIGCOI/reports/form-type-usage${qs(dateRange, dateFrom, dateTo)}`, signal),

  getPeakUsage: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<PeakUsageResponse>(
      `/api/IGIGCOI/reports/peak-usage${qs(dateRange, dateFrom, dateTo)}`, signal),

  getTopAgents: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string, top: number = 10) =>
    get<TopAgentsResponse>(
      `/api/IGIGCOI/reports/top-agents${qs(dateRange, dateFrom, dateTo, { top: String(top) })}`, signal),

  getStatusDistribution: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<StatusDistributionResponse>(
      `/api/IGIGCOI/reports/industry-completion${qs(dateRange, dateFrom, dateTo)}`, signal),

  getUserTypeBreakdown: (
    certType: string,
    dateRange: DateRange,
    signal?: AbortSignal,
    dateFrom?: string,
    dateTo?: string,
  ) =>
    get<UserTypeBreakdownResponse>(
      `/api/IGIGCOI/reports/user-type-breakdown${qs(dateRange, dateFrom, dateTo, { certType })}`, signal),

  getLobBreakdown: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<LobBreakdownResponse>(
      `/api/IGIGCOI/reports/lob-breakdown${qs(dateRange, dateFrom, dateTo)}`, signal),

  getAccess: (signal?: AbortSignal) =>
    get<{ hasAccess: boolean; userId: number }>(`/api/igigcoi/reports/access`, signal),

  getLoginActivity: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<LoginActivityResponse>(
      `/api/IGIGCOI/reports/login-activity${qs(dateRange, dateFrom, dateTo)}`, signal),

  getActivityHistory: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<ActivityHistoryResponse>(
      `/api/IGIGCOI/reports/activity-history${qs(dateRange, dateFrom, dateTo)}`, signal),

  getErrorSummary: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<ErrorSummaryResponse>(
      `/api/IGIGCOI/reports/error-summary${qs(dateRange, dateFrom, dateTo)}`, signal),

  getErrorsOverTime: (dateRange: DateRange, signal?: AbortSignal, dateFrom?: string, dateTo?: string) =>
    get<ErrorsOverTimeResponse>(
      `/api/IGIGCOI/reports/errors-over-time${qs(dateRange, dateFrom, dateTo)}`, signal),
};
